'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';

import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { BackButton } from '@/components/ui/BackButton';
import { getCurrencyInfo } from '@/lib/money';
import { usePromotionAnalytics } from '@/lib/api/promotionAnalytics';

import { PromotionAnalyticsFilters } from './components/PromotionAnalyticsFilters';
import { PromotionAnalyticsKpis } from './components/PromotionAnalyticsKpis';
import { PromotionAnalyticsTable } from './components/PromotionAnalyticsTable';
import { PromotionCodeExportButton } from './components/PromotionCodeExportButton';

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_RANGE_DAYS = 30;

function startOfDayUTC(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

function endOfDayUTC(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
}

const PromotionAnalyticsPage = () => {
  const { id: storeId, storeRoutes } = useStoreNavigate();
  const { t } = useTranslation('analytics');
  const [searchParams, setSearchParams] = useSearchParams();

  const { from, to } = React.useMemo(() => {
    const now = new Date();
    const defaultFrom = startOfDayUTC(new Date(now.getTime() - (DEFAULT_RANGE_DAYS - 1) * DAY_MS));
    const defaultTo = endOfDayUTC(now);
    const parse = (value: string | null, fallback: Date) => {
      if (!value) return fallback;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? fallback : parsed;
    };
    return {
      from: parse(searchParams.get('from'), defaultFrom),
      to: parse(searchParams.get('to'), defaultTo),
    };
  }, [searchParams]);

  const currencyParam = searchParams.get('currency') ?? undefined;

  const handleRangeChange = (range: DateRange) => {
    if (!range.from || !range.to) return;
    const next = new URLSearchParams(searchParams);
    next.set('from', startOfDayUTC(range.from).toISOString());
    next.set('to', endOfDayUTC(range.to).toISOString());
    setSearchParams(next, { replace: true });
  };

  const handleCurrencyChange = (currency: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('currency', currency);
    setSearchParams(next, { replace: true });
  };

  const { data, isLoading, isError } = usePromotionAnalytics(
    { storeId, startDate: from, endDate: to, currency: currencyParam },
    { enabled: !!storeId },
  );

  // Trust a server-confirmed currency first; otherwise only fall back to the URL
  // param when it is a structurally-known code. An unknown ?currency= (hand-crafted
  // URL) must never reach formatMoney/minorUnitsToMajorUnits, which throw on
  // currencies absent from the currency table.
  const normalizedParam = currencyParam?.toUpperCase();
  const requestedCurrency = normalizedParam && getCurrencyInfo(normalizedParam) ? normalizedParam : undefined;
  const resolvedCurrency = data?.currency ?? requestedCurrency ?? '';

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div className="flex items-center gap-2">
          <BackButton to={storeRoutes.PROMOTIONS()} />
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">{t('promotionAnalytics.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('promotionAnalytics.description')}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-2">
          <PromotionAnalyticsFilters
            range={{ from, to }}
            onRangeChange={handleRangeChange}
            currency={resolvedCurrency}
            availableCurrencies={data?.availableCurrencies ?? []}
            onCurrencyChange={handleCurrencyChange}
          />
          <PromotionCodeExportButton
            storeId={storeId}
            currency={resolvedCurrency}
            startDate={from}
            endDate={to}
          />
        </div>
      </div>

      <PromotionAnalyticsKpis summary={data?.summary} currency={resolvedCurrency} isLoading={isLoading} />

      <PromotionAnalyticsTable
        storeId={storeId}
        promotions={data?.promotions ?? []}
        currency={resolvedCurrency}
        startDate={from}
        endDate={to}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
};

const PromotionAnalyticsPageGuarded = withRequiredPermission(PromotionAnalyticsPage, PERMISSIONS.ANALYTICS_VIEW);
export default PromotionAnalyticsPageGuarded;
export { PromotionAnalyticsPageGuarded as PromotionAnalyticsPage };
